import { ReactElement } from "react";
import {
	GlobalSearchMappingStepConfigurationSaveModel,
	IntegrationFlowValidationResultGlobalSearchSampleModel
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { DateString, FormItem } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IInputAttachable, IObjectInputAttachable } from "client/Forms/attachable";
import { isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ResultMapperFormResult } from "./ResultMapperFormResult";
import { ExpressionTextInput } from "./ExpressionTextInput";

export type GlobalSearchFormProps = {
	attachable: IObjectInputAttachable<GlobalSearchMappingStepConfigurationSaveModel>;
	isReadOnly: boolean;
	timeZone: TimeZone;
	globalSearchSample: OptionalModel<IntegrationFlowValidationResultGlobalSearchSampleModel> | null;
};

export const GlobalSearchForm = (props: GlobalSearchFormProps): ReactElement => {
	const {
		attachable,
		isReadOnly,
		globalSearchSample,
		timeZone
	} = props;

	const titleExpressionHandler: IInputAttachable<string> = attachable.for("titleExpression");
	const linkExpressionHandler: IInputAttachable<string> = attachable.for("linkExpression");
	const dateExpressionHandler: IInputAttachable<string> = attachable.for("dateExpression");
	const subtitle1ExpressionHandler: IInputAttachable<string> = attachable.for("subtitle1Expression");
	const subtitle2ExpressionHandler: IInputAttachable<string> = attachable.for("subtitle2Expression");
	const descriptionExpressionHandler: IInputAttachable<string> = attachable.for("descriptionExpression");
	const hasResult: boolean = isNotNil(globalSearchSample)
		&& globalSearchSample.isSet;

	const globalSearchSampleValue: IntegrationFlowValidationResultGlobalSearchSampleModel | null = isNotNil(globalSearchSample) && globalSearchSample.isSet
		? globalSearchSample.value
		: null;

	return (
		<>
			<FormItem
				label={localize("_Title")}
				isRequired
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={titleExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={titleExpressionHandler}
					hasResult={hasResult}
					resultValue={globalSearchSampleValue?.title}
				/>
			</FormItem>
			<FormItem
				label={localize("_Link")}
				isRequired
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={linkExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={linkExpressionHandler}
					hasResult={hasResult}
					resultValue={globalSearchSampleValue?.link}
				/>
			</FormItem>
			<FormItem label={localize("_DateCreatedModified")}>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={dateExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={dateExpressionHandler}
					hasResult={hasResult}
					resultValue={globalSearchSampleValue?.dateTime}
				>
					{globalSearchSampleValue?.dateTime && (
						<DateString
							value={globalSearchSampleValue.dateTime}
							timezone={timeZone}
							format="RelativeDayWithTime"
						/>
					)}
				</ResultMapperFormResult>
			</FormItem>
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "1"
						}
					]
				}}
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={subtitle1ExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={subtitle1ExpressionHandler}
					hasResult={hasResult}
					resultValue={globalSearchSampleValue?.subTitle1}
				/>
			</FormItem>
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "2"
						}
					]
				}}
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={subtitle2ExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={subtitle2ExpressionHandler}
					hasResult={hasResult}
					resultValue={globalSearchSampleValue?.subTitle2}
				/>
			</FormItem>
			<FormItem label={localize("_Description")}>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={descriptionExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={descriptionExpressionHandler}
					hasResult={hasResult}
					resultValue={globalSearchSampleValue?.description}
				/>
			</FormItem>
		</>
	);
};
GlobalSearchForm.displayName = "GlobalSearchForm";